import { useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { DeviceData } from "../../../../../../API";
import WarningBox from "../../../../../../common/components/box/WarningBox";
import CloseDialogIcon from "../../../../../../common/components/icons/CloseDialogIcon";
import {
  PAN_DEFAULT,
  TILT_DEFAULT,
  ZOOM_DEFAULT,
} from "../../../../../../common/components/inputs/ptz/constants";
import ControlTable from "../../../../../../common/components/stream/ControlTable";
import { usePTController } from "../../../../../../common/hooks/usePTController";
import { useStream } from "../../../../../../common/hooks/useStream";
import { extractDeviceNameFromId } from "../../../../../../common/utils/getEntityName";
import { selectedStreamObjectVariable } from "../../../../../../common/variables/selectedStreamObject";
import { usePublishNode } from "../../../../../devices/hooks/usePublishNode";
import HLSStreamPlayer from "../cameraView/HLSStreamPlayer";
import PTZoneListTableContainer from "../devices/ptzonelist/PTZoneListTableContainer";
import {
  selectedZoneVariable,
  useFirstZoneVariable,
  useSelectedZoneVariable,
} from "../devices/ptzonelist/zones.variable";

interface LiveViewDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  deviceId: string;
  deviceName: string;
  locationName: string;
  currentDevice: string;
  nodeId: string;
  hasPanTilt: boolean;
  hasZoom: boolean;
  setIsReady: (value: boolean) => void;
  deviceData: DeviceData;
}

const LiveViewDialog = ({
  open,
  setOpen,
  deviceId,
  deviceName,
  locationName,
  currentDevice,
  nodeId,
  hasPanTilt,
  hasZoom,
  deviceData,
  setIsReady,
}: LiveViewDialogProps) => {
  const selectedZone = useSelectedZoneVariable();

  const firstZoneVariable = useFirstZoneVariable();

  const { getStreamName } = useStream();

  const { publishNode } = usePublishNode();

  const [ptOpen, setPtOpen] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const {
    pan,
    tilt,
    zoom,
    disabled,
    movePtUp,
    movePtDown,
    movePtLeft,
    movePtRight,
    handleGoToPt,
    handlePanInputChange,
    handleTiltInputChange,
    handleZoomInputChange,
    validationState,
  } = usePTController({
    deviceId,
    deviceName,
    nodeId,
    deviceData,
    zoneName: selectedZone.zoneName,
  });

  const { extractedDeviceName } = extractDeviceNameFromId(deviceId);

  const handleOpenPtControlsOnClick = (): void => {
    publishNode({
      message: JSON.stringify({
        TARGET: "PANTILT",
        ACTION: "START_PT_CONTROL",
        device_name: extractedDeviceName,
      }),
      nodeId,
    }).then((): void => {
      setPtOpen(true);
    });
  };

  const handleClose = (): void => {
    const firstZonePan = firstZoneVariable?.pan ?? PAN_DEFAULT;
    const firstZoneTilt = firstZoneVariable?.tilt ?? TILT_DEFAULT;
    const firstZoneZoom = firstZoneVariable?.zoom ?? ZOOM_DEFAULT;
    const firstZoneName = firstZoneVariable?.zoneName ?? "";

    setOpen(false);

    setIsReady(false);

    setPtOpen(false);

    const streamName = getStreamName(nodeId, deviceId);

    publishNode({
      message: JSON.stringify({
        TARGET: "KINESIS_STREAM",
        ACTION: "KINESIS_STREAM_END",
        streamName,
        deviceName: extractedDeviceName,
      }),
      nodeId,
    }).then((): void => {
      setIsReady(true);

      selectedStreamObjectVariable(null);
    });

    if (ptOpen) {
      handleGoToPt(firstZonePan, firstZoneTilt, firstZoneZoom, firstZoneName);

      publishNode({
        message: JSON.stringify({
          TARGET: "PANTILT",
          ACTION: "STOP_PT_CONTROL",
          device_name: extractedDeviceName,
        }),
        nodeId,
      }).then((): void => {
        selectedZoneVariable({
          zoneName: firstZoneName,
          pan: firstZonePan,
          tilt: firstZoneTilt,
          zoom: firstZoneZoom,
        });
      });
    }
  };

  const handleSetVideoError = () => {
    setVideoError(true);
  };

  const videoPlayerWidth = videoLoading || videoError ? "100%" : "auto";

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: "0px",
          maxWidth: "100%",
          maxHeight: "100%",
          width: "100%",
          height: "100%",
          margin: 0,
        },
      }}
      open={open}
      fullWidth
      onClose={handleClose}
    >
      <CloseDialogIcon onClose={handleClose} />
      <DialogContent
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          overflowY: "hidden",
        }}
      >
        <Box
          sx={{
            minWidth: "50%",
            display: "flex",
            width: videoPlayerWidth,
          }}
        >
          <HLSStreamPlayer
            setDialogStyles
            setVideoLoading={setVideoLoading}
            setVideoError={handleSetVideoError}
          />
        </Box>
        <Box
          sx={{
            padding: "2em",
            paddingBottom: 0,
            minWidth: "412px",
          }}
        >
          {ptOpen ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <WarningBox>
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5em",
                    alignItems: "center",
                  }}
                >
                  <ErrorOutlineIcon sx={{ color: "warning.main" }} />
                  <Typography
                    sx={{ color: "warning.main", "& p": { margin: 0 } }}
                    variant="body2Regular"
                    component="span"
                  >
                    <p>The model is stopped while you are</p>
                    <p>running PT controller</p>
                  </Typography>
                </Box>
              </WarningBox>

              <Divider
                sx={{
                  margin: ".7em 0",
                  visibility: "hidden",
                }}
              />

              <Typography
                sx={{
                  marginBottom: "1em",
                }}
                variant="subtitle1"
              >
                Move the camera position
              </Typography>

              <ControlTable
                pan={pan}
                tilt={tilt}
                zoom={zoom}
                ptDisabled={disabled}
                validation={validationState}
                showValidationAlert
                handlePanInputChange={handlePanInputChange}
                handleGoToPt={handleGoToPt}
                handleTiltInputChange={handleTiltInputChange}
                handleZoomInputChange={handleZoomInputChange}
                movePtLeft={movePtLeft}
                movePtRight={movePtRight}
                movePtUp={movePtUp}
                movePtDown={movePtDown}
                hasZoom={hasZoom}
              />

              <WarningBox>
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5em",
                    alignItems: "center",
                  }}
                >
                  <ErrorOutlineIcon
                    sx={{
                      color: "warning.main",
                    }}
                  />

                  <Typography
                    sx={{
                      color: "warning.main",
                      "& p": {
                        margin: 0,
                      },
                    }}
                    variant="body2Regular"
                    component="span"
                  >
                    <p>Please note that pan and tilt of device is</p>
                    <p>accurate to 0.1 degree</p>
                  </Typography>
                </Box>
              </WarningBox>

              <Divider
                sx={{
                  margin: ".7em 0",
                  visibility: "hidden",
                }}
              />

              <Typography variant="body1Bold" component="span">
                Observing zones
              </Typography>
              <PTZoneListTableContainer
                currentDeviceId={currentDevice}
                nodeId={nodeId}
                hasZoom={hasZoom}
                disableRowClick={disabled}
                handleGoToPt={handleGoToPt}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head" sx={{ border: 0, width: "80px" }}>
                      <Typography variant="inputLabel" component="span">
                        DEVICE
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0 }}>
                      <Typography variant="body2Bold" component="span">
                        {deviceName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head" sx={{ border: 0, width: "80px" }}>
                      <Typography variant="inputLabel" component="span">
                        LOCATION
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0 }}>
                      <Typography variant="body2Regular" component="span">
                        {locationName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box
                sx={{
                  marginTop: "auto",
                }}
              >
                {hasPanTilt && (
                  <>
                    <Button
                      sx={{
                        display: ptOpen ? "none" : "block",
                        color: "text.primary",
                        borderColor: "text.primary",
                        marginBottom: "1em",
                      }}
                      variant="outlined"
                      size="small"
                      onClick={handleOpenPtControlsOnClick}
                    >
                      <Typography variant="buttonMedium" component="span">
                        Open PT Controller
                      </Typography>
                    </Button>

                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5em",
                        paddingBottom: "2em",
                      }}
                    >
                      <ErrorOutlineIcon sx={{ color: "warning.main" }} />
                      <Typography
                        sx={{ color: "warning.main", "& p": { margin: 0 } }}
                        variant="body2Regular"
                        component="span"
                      >
                        <p>
                          All models are stopped while you are running pt
                          control
                        </p>
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LiveViewDialog;
